<template>
  <div
    id="input-container"
    :class="{
      'border-warning': isEditing && validations.length > 0,
      'border-danger': validation.error && validations.length > 0,
      'border-success': computedIsModified && validations.length > 0
    }"
    @click="startEditing"
  >
    <div
      class="input-label d-inline-block"
      :class="{ 'input-label-as-placeholder': !computedShowInput }"
    >
      {{ label }}
    </div>
    <div class="d-flex justify-content-between">
      <i
        class="icon input-icon"
        :class="computedShowInput ? 'input-icon-hidden ' + icon : icon"
      />
      <input
        v-model="value"
        ref="input-main"
        class="input-main text-truncate"
        :class="computedTooltipMessage != null ? 'w-85' : ''"
        :id="identifier"
        :type="isProtected ? 'password' : 'text'"
        :name="identifier"
        @keydown="bindPreviousValue"
        @focus="startEditing"
        @input="onInputEvent"
        @blur="stopEditing"
      />
      <TooltipInformation
        :identifier="identifier"
        :message="computedTooltipMessage"
        :icon="computedTooltipIcon"
        :tooltipBodyColor="computedTooltiBodyColor"
        :iconColor="computedTooltipIconColor"
      />
    </div>
  </div>
</template>

<script>
import { TooltipInformation } from "@/components";

/**
 * @description Composante générique pour les inputs de type texte permettant d'obtenir de l'information de l'utilisateur
 * @author Samuel Dubé
 * @author Alexandre Gallant
 * @vue-prop {String} label - Étiquette affichée pour l'input
 * @vue-prop {String} icon - Icône affichée pour l'input
 * @vue-prop {String} identifier - Identifiant de l'input
 * @vue-prop {Array} validations - Validations à effectuer une fois la saisie terminée
 * @vue-prop {String} allowedChars - Caractères permis pour l'input
 * @vue-prop {Number} maxLength - Longueur maximale permise pour la valeur
 * @vue-prop {Function} transformationHook - Méthode permettant d'appliquer une modification à la valeur
 * @vue-prop {Boolean} isProtected - Indique si un input est avec du contenu protégé
 * @vue-prop {String} initialValue - Valeur initiale de la saisie
 * @vue-prop {String} informationTooltipContent - Le message à afficher pour le tooltip d'information (null pour qu'il n'y en ait pas)
 * @vue-prop {Number} forcedValidations - Nombre de validations forcées (les validations sont exécutées lorsque la valeur change)
 * @vue-data {Boolean} isModified - Indique que l'input est modifié
 * @vue-data {Boolean} isEditing - Indique que l'input est en cours d'édition
 * @vue-data {String} value - Valeur de l'input
 * @vue-data {String} previousValue - Ancienne valeur de l'input
 * @vue-data {Object} validation - Objet de validation pour l'input
 * @vue-coomputed {Boolean} computedShowInput - Permet de savoir s'il faut afficher l'input
 * @vue-computed {Boolean} computedIsModified - Permet de savoir si l'input a été modifié
 * @vue-computed {String} computedTooltipMessage - Le texte à afficher dans le tooltip
 * @vue-computed {String} computedTooltipIcon - L'icône à afficher pour le tooltip
 * @vue-computed {String} computedTooltiBodyColor - La couleur du tooltip
 * @vue-computed {String} computedTooltipIconColor - La couleur de l'icône du tooltip
 * @vue-event {Object} onInput - Émet que le contenu de l'input a été modifié
 * @vue-event {Object} onChange - Émet que le contenu de l'input a été modifié et que l'input a perdu le focus
 */
export default {
  name: "InputText",
  components: {
    TooltipInformation
  },
  props: {
    label: {
      type: String,
      required: true
    },
    icon: {
      type: String,
      required: true
    },
    identifier: {
      type: String,
      required: true
    },
    validations: {
      type: Array,
      required: false,
      default: () => []
    },
    allowedChars: {
      type: String,
      required: false,
      default: "\\s|\\S"
    },
    maxLength: {
      type: Number,
      required: false,
      default: 100
    },
    transformationHook: {
      type: Function,
      required: false,
      default: null
    },
    isProtected: {
      type: Boolean,
      required: false,
      default: false
    },
    initialValue: {
      type: String,
      required: false,
      default: ""
    },
    informationTooltipContent: {
      type: String,
      required: false,
      default: null
    },
    forcedValidations: {
      type: Number,
      required: false,
      default: 0
    }
  },
  watch: {
    /**
     * @description Exécute les validation
     */
    forcedValidations: function() {
      this.validation = this.executeValidations();
    }
  },
  data() {
    return {
      isModified: false,
      isEditing: false,
      value: this.initialValue,
      previousValue: null,
      validation: this.getInitialValidation()
    };
  },
  computed: {
    computedShowInput() {
      return this.value !== "" || this.isEditing;
    },
    computedIsModified() {
      return this.value !== "" && !this.validation.error;
    },
    computedTooltipMessage() {
      return this.validation.isValid
        ? this.informationTooltipContent
        : this.validation.error;
    },
    computedTooltipIcon() {
      return this.validation.isValid
        ? "question circle outline"
        : "exclamation circle";
    },
    computedTooltiBodyColor() {
      return this.validation.isValid ? "info" : "danger";
    },
    computedTooltipIconColor() {
      return this.validation.isValid ? "dark" : "danger";
    }
  },
  methods: {
    /**
     * @description Méthode commencant l'édition de l'input
     */
    startEditing() {
      this.isEditing = true;
      this.setInputFocus();
      this.validation = this.getInitialValidation();
    },

    /**
     * @description Méthode arrêtant l'édition de l'input
     */
    stopEditing() {
      this.isEditing = false;
      this.validation = this.executeValidations();
      this.emitOnChangeEvent();
    },

    /**
     * @description Méthode qui met le focus sur l'input
     */
    setInputFocus() {
      // On utilise le next tick puisque l'input est masqué
      this.$nextTick(function() {
        this.$refs["input-main"].focus();
      });
    },

    /**
     * @description Méthode retounant l'objet de validation initial
     * @returns {Object} Objet initial de la validation
     */
    getInitialValidation() {
      return {
        isValid: true,
        error: null
      };
    },

    /**
     * @description Méthode qui éxecute toutes les validations passées
     * en props à la composante et retourne un objet contenant le résultat
     * de la validation
     * @returns {Object} Objet résultant de la validation
     */
    executeValidations() {
      let validation = this.getInitialValidation();
      for (let i = 0; i < this.validations.length; i++) {
        if (!this.validations[i].exec(this.value)) {
          validation.error = this.validations[i].error;
          validation.isValid = false;
          break;
        }
      }
      return validation;
    },

    /**
     * @description Méthode vérifiant si le caractère entré est valide
     * @param {String} char Le caractère à tester
     * @returns {Boolean} Vrai si le caractère est permis
     */
    isCharValid(char) {
      // Inverse la validation du regex
      const pattern = "[" + this.allowedChars + "\b]";
      const regex = new RegExp(pattern, "gi");
      return regex.test(char);
    },

    /**
     * @description Méthode qui applique la fonction de transformation à
     * la valeur
     * @param {String} value La nouvelle valeur de l'input
     * @param {String} oldValue La valeur de l'input avant l'ajout du nouveau caractère
     * @param {String} inputChar Le caractère à ajouter
     * @returns {String|Number} Valeur transformée
     */
    applyTransformationHook(value, oldValue, inputChar) {
      if (this.transformationHook) {
        value = this.transformationHook(value, oldValue, inputChar);
      }
      return value;
    },

    /**
     * @description Méthode qui applique la longueur maximale à la valeur
     * @param {String} value La valeur à traiter
     * @returns {String} Valeur conforme à la longueur maximale
     */
    applyMaxLength(value) {
      if (value.length > this.maxLength) {
        value = this.removeLastChar(value);
      }
      return value;
    },

    /**
     * @description Méthode qui retire le dernier caractère de la valeur
     * @param {String} value La valeur à traiter
     * @returns {String} Valeur sans le dernier caractère
     */
    removeLastChar(value) {
      return value.substring(0, value.length - 1);
    },

    /**
     * @description Méthode qui créer la prochaine valeur à appliquer
     * à la valeur courante
     * @param {String} orignalNextValue Prochaine valeur théorique de l'input
     * @param {String} previousValue La valeur de l'input avant l'ajout du nouveau caractère
     * @param {String} nextChar Le caractère à ajouter
     * @returns {String} Prochaine valeur
     */
    createNextValue(orignalNextValue, previousValue, nextChar) {
      let nextValue = orignalNextValue;
      nextValue = this.applyMaxLength(nextValue);
      nextValue = this.applyTransformationHook(
        nextValue,
        previousValue,
        nextChar
      );
      return nextValue;
    },

    /**
     * @description Méthode qui sauvegarde l'ancienne valeur
     */
    bindPreviousValue() {
      this.previousValue = this.value;
    },

    /**
     * @description Event méthode lancée par le changement de l'input
     * @param {Object} event L'événement qui a mené à l'appel de cette fonction
     *
     */
    onInputEvent(event) {
      if (!event.data) {
        // Quand on appuie sur backspace
        this.value = this.createNextValue(
          event.target.value,
          this.previousValue,
          event.data
        );
      } else if (this.isCharValid(event.data)) {
        // Si le caractère entré est bon
        this.value = this.createNextValue(
          this.value,
          this.previousValue,
          event.data
        );
      } else {
        this.value = this.previousValue;
      }
      const eventData = this.createOnChangeData();
      this.$emit("onInput", eventData);
    },

    /**
     * @description Event méthode qui envoie l'information du composant
     * à son parent
     */
    emitOnChangeEvent() {
      const eventData = this.createOnChangeData();
      this.$emit("onChange", eventData);
    },

    /**
     * @description Méthode qui créer l'objet événement à envoyer au parent
     * @returns {Object} Objet événement
     */
    createOnChangeData() {
      return {
        name: this.identifier,
        value: this.value,
        validation: {
          isValid: this.validation.isValid,
          error: this.validation.error
        }
      };
    }
  }
};
</script>

<style scoped lang="scss">
#input-container {
  cursor: text;
  border-bottom: 1px solid $gray-600;
  color: $gray-600;
  padding-bottom: 0.6em;
  overflow: hidden;
  position: relative;
  transition: all 0.15s ease-in-out;
}

.input-main {
  width: 100%;
  position: absolute;
  left: 0;
}

.input-label {
  transform: translateX(-5%) scale(0.9);
}

.input-tooltip {
  cursor: pointer;
  z-index: 100;
}

.input-label,
.input-icon {
  transition: all 0.15s ease-in-out;
  pointer-events: none;
}

.input-icon-hidden {
  transform: translateX(-105%);
}

.input-label-as-placeholder {
  transform: translateY(100%);
  margin-left: 1.5em;
  font-size: $font-size-base;
  pointer-events: initial;
}

input {
  appearance: none;
  border: none;
  width: 90%;
  background-color: transparent;
  &:focus {
    outline: none;
  }
}
</style>
